import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui';
import Modal from '../modal/Modal';
import { HOST, PORT } from '../../environment';
import { formatOutageRanges, hasOutageCoordinates, outageGroupChoices } from './outageSchedule';
import styles from './OutageScheduleButton.module.css';

const LABEL_STYLE = {
  fontSize: '24px',
  fontWeight: '300',
  letterSpacing: '2px',
};

function daySummary(day, t) {
  if (!day || day.status === 'WaitingForSchedule') {
    return t('outageSchedule.waiting');
  }
  if (day.status === 'EmergencyShutdowns') {
    return t('outageSchedule.emergency');
  }
  const ranges = formatOutageRanges(day.outages);
  if (!ranges) {
    return t('outageSchedule.noOutages');
  }
  return ranges;
}

function errorText(code, t) {
  if (code === 'REGION_UNSUPPORTED') return t('outageSchedule.unsupported');
  if (code === 'GROUP_UNKNOWN') return t('outageSchedule.groupUnknown');
  if (code === 'ADDRESS_NOT_FOUND') return t('outageSchedule.notFound');
  return t('outageSchedule.error');
}

function addressHasHouse(address) {
  if (!address) return false;
  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  return parts.length >= 3 && /^\d/.test(parts[parts.length - 2]);
}

function formatUpdated(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { timeZone: 'Europe/Kyiv' });
}

/**
 * Asks the backend for the outage schedule at the port GPS.
 * The network request runs only after a click.
 */
export default function OutageScheduleButton({
  latitude,
  longitude,
  stationNumber,
  className,
  style,
  labelStyle,
  labelClassName,
  variant = 'outline',
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  if (!hasOutageCoordinates(latitude, longitude)) {
    return null;
  }

  async function handleClick() {
    if (loading) return;
    setOpen(true);
    setLoading(true);
    setResult(null);
    setFailed(false);
    setSelectedGroup(null);
    try {
      const params = new URLSearchParams({
        latitude: String(latitude),
        longitude: String(longitude),
      });
      if (stationNumber) {
        params.set('station_number', String(stationNumber));
      }
      const response = await fetch(`${HOST}:${PORT}/api/outage/schedule?${params.toString()}`);
      if (!response.ok) {
        setFailed(true);
        return;
      }
      setResult(await response.json());
    } catch (_error) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }

  const schedule = result?.success ? result : null;
  const message = failed ? t('outageSchedule.error') : result && !result.success ? errorText(result.error, t) : '';
  const groupChoices = schedule?.queues?.length ? outageGroupChoices(schedule.queues) : [];
  const selectedChoice = groupChoices.find(choice => choice.group === selectedGroup) || null;

  return (
    <>
      <Button
        type="button"
        variant={variant}
        className={`${styles.button}${className ? ` ${className}` : ''}`}
        style={style}
        disabled={loading}
        aria-busy={loading}
        onClick={handleClick}
      >
        <span className={labelClassName} style={labelStyle === undefined ? LABEL_STYLE : labelStyle || undefined}>
          {t('outageSchedule.button')}
        </span>
      </Button>
      {open && (
        <Modal setModalOpen={setOpen} modalOpen={open} scrollable>
          <h1 className="delayedStartWarning">{t('outageSchedule.title')}</h1>
          {loading ? (
            <p style={{ fontSize: '18px', marginBottom: '24px', color: '#fff9', lineHeight: '1.5' }}>
              {t('outageSchedule.loading')}
            </p>
          ) : null}
          {!loading && schedule ? (
            <div
              style={{ fontSize: '18px', marginBottom: '24px', color: '#fff9', lineHeight: '1.5', textAlign: 'left' }}
            >
              {schedule.address ? <p style={{ marginBottom: '8px' }}>{schedule.address}</p> : null}
              {groupChoices.length ? (
                selectedChoice ? (
                  <>
                    <p style={{ marginBottom: '12px' }}>{t('outageSchedule.group', { group: selectedChoice.group })}</p>
                    <p style={{ marginBottom: '8px' }}>
                      {t('outageSchedule.today')}: {daySummary(selectedChoice.today, t)}
                    </p>
                    <p style={{ marginBottom: '16px' }}>
                      {t('outageSchedule.tomorrow')}: {daySummary(selectedChoice.tomorrow, t)}
                    </p>
                    <button type="button" className={styles.another} onClick={() => setSelectedGroup(null)}>
                      {t('outageSchedule.anotherGroup')}
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ marginBottom: '12px' }}>
                      {t(
                        addressHasHouse(schedule.address) ? 'outageSchedule.queuesHint' : 'outageSchedule.chooseGroup'
                      )}
                    </p>
                    <div className={styles.groups}>
                      {groupChoices.map(choice => (
                        <button
                          key={choice.group}
                          type="button"
                          className={styles.group}
                          onClick={() => setSelectedGroup(choice.group)}
                        >
                          {choice.group}
                        </button>
                      ))}
                    </div>
                  </>
                )
              ) : (
                <>
                  <p style={{ marginBottom: '12px' }}>
                    {schedule.group
                      ? t('outageSchedule.group', { group: schedule.group })
                      : t('outageSchedule.allGroups')}
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    {t('outageSchedule.today')}: {daySummary(schedule.today, t)}
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    {t('outageSchedule.tomorrow')}: {daySummary(schedule.tomorrow, t)}
                  </p>
                </>
              )}
              {schedule.updatedOn ? (
                <p style={{ marginBottom: '12px' }}>
                  {t('outageSchedule.updated', { time: formatUpdated(schedule.updatedOn) })}
                </p>
              ) : null}
            </div>
          ) : null}
          {!loading && message ? (
            <div
              style={{ fontSize: '18px', marginBottom: '24px', color: '#fff9', lineHeight: '1.5', textAlign: 'left' }}
            >
              {result?.address ? <p style={{ marginBottom: '8px' }}>{result.address}</p> : null}
              <p>{message}</p>
            </div>
          ) : null}
          <Button
            onClick={() => setOpen(false)}
            className="mainBtn buttonForOfflineStation"
            style={{ display: 'block', margin: '0 auto' }}
          >
            <span className="mainBtnText">OK</span>
          </Button>
        </Modal>
      )}
    </>
  );
}
